import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
export interface TaskActivityWhisperData {
  userId: string;
  taskId?: string;
  timestamp: number;
}
// Metadata only - no actual channel instance
export interface PresenceChannelMeta {
  id: string;
  subscribers: Record<string, true>; // Component IDs that are using this channel
  createdAt: number;
}

// Updated state interface - no channel instances
interface PresenceState {
  channels: Record<string, PresenceChannelMeta>;
  activeTask: TaskActivityWhisperData | null;
}

const initialState: PresenceState = {
  channels: {},
  activeTask: null,
};

const presenceSlice = createSlice({
  name: 'presence',
  initialState,
  reducers: {
    // Add or get an existing presence channel metadata
    addPresenceChannelMeta: (
      state,
      action: PayloadAction<{
        channelId: string;
        subscriberId: string;
      }>
    ) => {
      const { channelId, subscriberId } = action.payload;

      if (state.channels[channelId]) {
        // Channel exists, add subscriber
        state.channels[channelId].subscribers[subscriberId] = true;
      } else {
        // Create new channel metadata
        state.channels[channelId] = {
          id: channelId,
          subscribers: { [subscriberId]: true },
          createdAt: Date.now(),
        };
      }
    },

    // Remove a subscriber from a presence channel
    removePresenceSubscriber: (
      state,
      action: PayloadAction<{
        channelId: string;
        subscriberId: string;
      }>
    ) => {
      const { channelId, subscriberId } = action.payload;

      if (state.channels[channelId]) {
        delete state.channels[channelId].subscribers[subscriberId];

        // If no more subscribers, remove the channel metadata
        if (Object.keys(state.channels[channelId].subscribers).length === 0) {
          delete state.channels[channelId];
        }
      }
    },

    // Clear all presence channels
    clearAllPresenceChannels: (state) => {
      state.channels = {};
    },

    // Clear channels for a specific site
    clearSitePresenceChannels: (state, action: PayloadAction<string>) => {
      const siteId = action.payload;
      const channelPattern = `pages.${siteId}`;

      Object.keys(state.channels).forEach((channelId) => {
        if (channelId.startsWith(channelPattern)) {
          delete state.channels[channelId];
        }
      });
    },

    setActiveTask: (state, action: PayloadAction<TaskActivityWhisperData>) => {
      state.activeTask = action.payload;
    },

    clearActiveTask: (state) => {
      state.activeTask = null;
    },
  },
});

export const {
  addPresenceChannelMeta,
  removePresenceSubscriber,
  clearAllPresenceChannels,
  clearSitePresenceChannels,
  setActiveTask,
  clearActiveTask,
} = presenceSlice.actions;

// Updated selectors
export const getPresenceChannelMeta =
  (channelId: string) => (state: RootState) =>
    state.presence.channels[channelId];

export const getAllPresenceChannels = (state: RootState) =>
  state.presence.channels;

export const getPresenceChannelSubscriberCount =
  (channelId: string) => (state: RootState) =>
    Object.keys(state.presence.channels[channelId]?.subscribers || {}).length;

export const getActiveTask = (state: RootState) => state.presence.activeTask;

export default presenceSlice.reducer;
